import { c as _c } from "react/compiler-runtime";
import { IcoTrash16 } from '@onefootprint/icons';
import type { BranchComparisonOperator, NodeConfigurationContextBranch } from '@onefootprint/request-types/dashboard';
import { Form, IconButton, Tooltip } from '@onefootprint/ui';
import startCase from 'lodash/startCase';
import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import EditableBranchName from '@/components/playbook-builder/components/conditional-node/components/editable-branch-name';
import useNodeContext from '@/components/playbook-builder/hooks/use-node-context';
import type { BranchFormData, FormBranchCase } from '../../../types';
import getBranch from '../../../utils/get-branch';
import getSupportedValues from '../../../utils/get-supported-values';
type BranchProps = {
  canEdit: boolean;
  field: FormBranchCase;
  index: number;
  config: NodeConfigurationContextBranch['config'] | undefined;
  handleDelete: () => void;
  disableDelete: boolean;
};

/** Compute a good default name for the branch branching on the provided `value` with the given `operator` */
const computeBranchName = ({
  value,
  operator
}: {
  value: string;
  operator: string;
}) => operator === '=' ? startCase(value) : `${operator} ${startCase(value)}`;
export const Branch = t0 => {
  const $ = _c(65);
  const {
    canEdit,
    field,
    index,
    config,
    handleDelete,
    disableDelete
  } = t0;
  const {
    t
  } = useTranslation("playbook-builder");
  const {
    control,
    setValue,
    getValues
  } = useFormContext();
  const successorId = useWatch({
    control,
    name: `cases.${index}.branch.successor`
  }) ?? undefined;
  const branchOn = useWatch({
    control,
    name: "branchPredicateId"
  }) ?? undefined;
  const currentConfig = getBranch(branchOn, config);
  const {
    node: successor
  } = useNodeContext(successorId);
  const operatorTranslations = {
    equals: t(_temp),
    not_equals: t(_temp2),
    greater_than: t(_temp3),
    less_than: t(_temp4),
    greater_than_or_equal: t(_temp5),
    less_than_or_equal: t(_temp6),
    in: t(_temp7),
    not_in: t(_temp8)
  };
  const defaultNameManuallyEdited = !!field.branch.name && field.branch.name !== computeBranchName({
    value: String(field.condition.value),
    operator: operatorTranslations[field.condition.op]
  });
  const [nameManuallyEdited, setNameManuallyEdited] = useState(defaultNameManuallyEdited);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setNameManuallyEdited(true);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleManualNameChange = t1;
  const handleSetBranchName = t2 => {
    const {
      value,
      operator
    } = t2;
    if (!nameManuallyEdited) {
      const branchName = computeBranchName({
        value: String(value ?? getValues(`cases.${index}.condition.value`)),
        operator: operatorTranslations[operator ?? getValues(`cases.${index}.condition.op`)]
      });
      setValue(`cases.${index}.branch.name`, branchName);
    }
  };
  const currentConfigOperators = currentConfig?.supportedOperators?.map(operator_0 => ({
    value: operator_0,
    label: operatorTranslations[operator_0]
  }));
  const operatorOptions = currentConfigOperators?.map(_temp9) ?? [];
  let t3;
  if ($[1] !== branchOn || $[2] !== config) {
    const branchOptions = config?.map(_temp10) ?? [];
    t3 = branchOptions.find(branch_0 => branch_0.value === branchOn)?.label;
    $[1] = branchOn;
    $[2] = config;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const branchOnLabel = t3;
  const supportedValues = getSupportedValues(currentConfig);
  const supportedValuesOptions = supportedValues.map(_temp11);
  let t4;
  if ($[4] !== disableDelete || $[5] !== successor?.kind || $[6] !== successorId || $[7] !== t) {
    const getDeleteDisabledReason = () => {
      if (disableDelete) {
        return t(_temp12);
      }
      if (successorId && successor?.kind !== "placeholder") {
        return t(_temp13);
      }
    };
    t4 = getDeleteDisabledReason();
    $[4] = disableDelete;
    $[5] = successor?.kind;
    $[6] = successorId;
    $[7] = t;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const disabledReason = t4;
  let t5;
  if ($[9] !== t) {
    t5 = t(_temp14);
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t5) {
    t6 = <span className="text-tertiary">{t5}:</span>;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = `cases.${index}.branch.name`;
  let t8;
  if ($[13] !== canEdit || $[14] !== handleManualNameChange || $[15] !== t7) {
    t8 = <EditableBranchName canEdit={canEdit} fieldName={t7} onNameChange={handleManualNameChange} />;
    $[13] = canEdit;
    $[14] = handleManualNameChange;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t6 || $[18] !== t8) {
    t9 = <div className="text-label-2 text-primary flex items-center gap-2 w-full min-h-[32px] whitespace-nowrap max-md:min-w-0">{t6}{t8}</div>;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== canEdit || $[21] !== disabledReason || $[22] !== handleDelete || $[23] !== index || $[24] !== t) {
    t10 = canEdit && <Tooltip content={disabledReason} disabled={!disabledReason}><IconButton data-testid="delete-button" icon={IcoTrash16} type="button" onClick={handleDelete} aria-label={t(_temp15, {
        index: index + 1
      })} destructive={true} disabled={!!disabledReason} /></Tooltip>;
    $[20] = canEdit;
    $[21] = disabledReason;
    $[22] = handleDelete;
    $[23] = index;
    $[24] = t;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <div className="flex flex-row bg-secondary border-b-tertiary p-2 items-center gap-3">{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t) {
    t12 = t(_temp16);
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t12) {
    t13 = <span className="mr-2">{t12}</span>;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== branchOnLabel) {
    t14 = <span className="text-snippet-2 text-primary monospace">{branchOnLabel}</span>;
    $[33] = branchOnLabel;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14) {
    t15 = <div className="content-center h-[32px] max-md:min-w-0 max-md:max-w-full"><div className="text-body-2 text-tertiary whitespace-nowrap max-md:truncate">{t13}{t14}</div></div>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const t16 = `cases.${index}.condition.op`;
  const t17 = !branchOn;
  const t18 = index + 1;
  let t19;
  if ($[38] !== t || $[39] !== t18) {
    t19 = t(_temp17, {
      index: t18
    });
    $[38] = t;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== handleSetBranchName) {
    t20 = value_1 => handleSetBranchName({
      operator: value_1 as BranchComparisonOperator
    });
    $[41] = handleSetBranchName;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== operatorOptions || $[44] !== t16 || $[45] !== t17 || $[46] !== t19 || $[47] !== t20) {
    t21 = <div className="w-[80px]"><Form.Select name={t16} size="compact" disabled={t17} aria-label={t19} options={operatorOptions} onChange={t20} /></div>;
    $[43] = operatorOptions;
    $[44] = t16;
    $[45] = t17;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== branchOn || $[50] !== config || $[51] !== getValues || $[52] !== handleSetBranchName || $[53] !== index || $[54] !== supportedValues.length || $[55] !== supportedValuesOptions || $[56] !== t) {
    t22 = supportedValues.length > 0 ? <Form.Select disabled={!branchOn} name={`cases.${index}.condition.value`} size="compact" aria-label={t(_temp18, {
      index: index + 1
    })} options={supportedValuesOptions} placeholder={t(_temp19)} rules={{
      required: t(_temp20)
    }} onChange={value_2 => handleSetBranchName({
      value: value_2
    })} /> : <Form.Input disabled={!branchOn} name={`cases.${index}.condition.value`} size="compact" placeholder={t(_temp21)} aria-label={t(_temp22, {
      index: index + 1
    })} rules={{
      validate: value_3 => {
        if (value_3 === "" || value_3 === null || value_3 === undefined) {
          return t(_temp23);
        }
        const branchPredicateId = getValues("branchPredicateId");
        const branchPredicate = getBranch(branchPredicateId, config);
        if (branchPredicate?.kind === "number" && Number.isNaN(Number(value_3))) {
          return t(_temp24);
        }
        return true;
      }
    }} onChange={e => handleSetBranchName({
      value: e.target.value
    })} />;
    $[49] = branchOn;
    $[50] = config;
    $[51] = getValues;
    $[52] = handleSetBranchName;
    $[53] = index;
    $[54] = supportedValues.length;
    $[55] = supportedValuesOptions;
    $[56] = t;
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== t15 || $[59] !== t21 || $[60] !== t22) {
    t23 = <div className="flex flex-row flex-wrap p-3 gap-2 w-full items-top md:flex-nowrap">{t15}{t21}{t22}</div>;
    $[58] = t15;
    $[59] = t21;
    $[60] = t22;
    $[61] = t23;
  } else {
    t23 = $[61];
  }
  let t24;
  if ($[62] !== t11 || $[63] !== t23) {
    t24 = <div className="bg-primary border border-tertiary rounded flex flex-col w-full">{t11}{t23}</div>;
    $[62] = t11;
    $[63] = t23;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  return t24;
};
function _temp($) {
  return $.branchNode.operators.equals;
}
function _temp2($_0) {
  return $_0.branchNode.operators.not_equals;
}
function _temp3($_1) {
  return $_1.branchNode.operators.greater_than;
}
function _temp4($_2) {
  return $_2.branchNode.operators.less_than;
}
function _temp5($_3) {
  return $_3.branchNode.operators.greater_than_or_equal;
}
function _temp6($_4) {
  return $_4.branchNode.operators.less_than_or_equal;
}
function _temp7($_5) {
  return $_5.branchNode.operators.in;
}
function _temp8($_6) {
  return $_6.branchNode.operators.not_in;
}
function _temp9(operator_1) {
  return {
    value: operator_1.value,
    label: operator_1.label
  };
}
function _temp10(branch) {
  return {
    value: branch.identifier,
    label: branch.displayName
  };
}
function _temp11(value_0) {
  return {
    value: value_0,
    label: value_0
  };
}
function _temp12($_7) {
  return $_7.branchNode.deleteDisabledOnlyBranch;
}
function _temp13($_8) {
  return $_8.branchNode.deleteDisabledReasonNonEmpty;
}
function _temp14($_9) {
  return $_9.branchNode.branch;
}
function _temp15($_10) {
  return $_10.branchNode.removeBranchAriaLabel;
}
function _temp16($_11) {
  return $_11.branchNode.if;
}
function _temp17($_12) {
  return $_12.branchNode.operatorSelect;
}
function _temp18($_13) {
  return $_13.branchNode.valueSelect;
}
function _temp19($_14) {
  return $_14.branchNode.selectValue;
}
function _temp20($_15) {
  return $_15.branchNode.valueRequired;
}
function _temp21($_16) {
  return $_16.branchNode.enterValue;
}
function _temp22($_17) {
  return $_17.branchNode.valueInput;
}
function _temp23($_18) {
  return $_18.branchNode.valueRequired;
}
function _temp24($_19) {
  return $_19.branchNode.valueRequiredNumber;
}